import "./widget-theme.css";
import {
  useEffect,
  useId,
  useRef,
  useState,
  useSyncExternalStore,
  type CSSProperties,
  type ReactNode,
} from "react";
import { ChevronDown, ChevronLeft, MessageCircle, X } from "lucide-react";
import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import { Card } from "@/shared/components/ui/card";
import { cn } from "@/shared/lib/utils";
import { useVisualViewport } from "../hooks/use-visual-viewport";
import { useChatStore } from "@/store/chat";
import { AiChip, Avatar } from "./avatar";
import { ChatWindowContext } from "./chat-window-context";
import { containMobileFocus } from "./mobile-focus";
import { HOVER_MUTED_TEXT } from "./styles";

const HEADER_BUTTON_CLASS = cn(
  "min-h-11 min-w-11 shrink-0 cursor-pointer",
  HOVER_MUTED_TEXT,
);

const mobileSnapshot = () => window.innerWidth < 640;
// Taller than any browser toolbar change, shorter than any on-screen keyboard.
const KEYBOARD_MIN_PX = 120;
const subscribeWidth = (listener: () => void) => {
  window.addEventListener("resize", listener);
  return () => window.removeEventListener("resize", listener);
};

export function WidgetFrame({
  children,
  unread = 0,
  muted = false,
  title = "Chats",
  subtitle,
  onBack,
  onTitleClick,
  rosterOpen,
  ai = false,
  headerActions,
}: {
  children: ReactNode;
  unread?: number;
  muted?: boolean;
  title?: string;
  subtitle?: string;
  onBack?: () => void;
  onTitleClick?: () => void;
  rosterOpen?: boolean;
  ai?: boolean;
  headerActions?: ReactNode;
  identityName?: string;
  demo?: boolean;
}) {
  const viewport = useVisualViewport();
  const mobile = useSyncExternalStore(subscribeWidth, mobileSnapshot);
  const keyboard =
    mobile && window.innerHeight - viewport.height > KEYBOARD_MIN_PX;
  const open = useChatStore((state) => state.open);
  const expanded = useChatStore((state) => state.expanded);
  const setOpen = useChatStore((state) => state.setOpen);
  const launcher = useRef<HTMLButtonElement>(null);
  const showHandlerTip = useChatStore((state) => state.handlerTip);
  const setShowHandlerTip = useChatStore((state) => state.setHandlerTip);
  const [panel, setPanel] = useState<HTMLElement | null>(null);
  const previousFocus = useRef<HTMLElement | null>(null);
  const panelId = useId();
  const titleId = useId();
  useEffect(() => {
    if (!open || !panel || !mobile) return;
    return containMobileFocus(panel);
  }, [open, panel, mobile]);
  useEffect(() => {
    if (!open) return;
    const keydown = (event: KeyboardEvent) => {
      if (
        event.key === "Escape" &&
        !event.defaultPrevented &&
        panel?.contains(document.activeElement) &&
        !panel.querySelector('[role="dialog"][aria-modal="true"]')
      ) {
        event.preventDefault();
        setOpen(false);
        requestAnimationFrame(() => {
          (previousFocus.current?.isConnected
            ? previousFocus.current
            : launcher.current
          )?.focus();
        });
      }
    };
    document.addEventListener("keydown", keydown);
    if (!panel?.contains(document.activeElement)) panel?.focus();
    return () => document.removeEventListener("keydown", keydown);
  }, [open, panel, setOpen]);
  const close = () => {
    setOpen(false);
    // Inert is removed by the mobile effect cleanup before restoring focus.
    requestAnimationFrame(() => launcher.current?.focus());
  };
  const heading = (
    <>
      {onBack && <Avatar name={title} ai={ai} />}
      <div className="min-w-0 flex-1 py-1 text-left">
        <h2
          id={titleId}
          className={cn(
            "m-0 flex items-center gap-1.5 overflow-hidden text-base font-semibold text-ellipsis whitespace-nowrap",
            !onBack &&
              "font-mono text-sm tracking-[0.18em] text-gold uppercase",
          )}
        >
          <span className="min-w-0 truncate">{title}</span>
          {ai && <AiChip />}
        </h2>
        {subtitle && (
          <p className="m-0 truncate text-xs font-normal text-muted-foreground">
            {subtitle}
          </p>
        )}
      </div>
      {onTitleClick && (
        <ChevronDown
          className={cn(
            "size-4 shrink-0 text-muted-foreground max-[360px]:hidden",
            rosterOpen && "rotate-180",
          )}
        />
      )}
    </>
  );
  return (
    <div
      className={cn(
        // Dark-only by ruling; see docs/handler-widget-implementation.md.
        "sm-chat-widget pointer-events-none fixed z-[2147483000] flex flex-col items-end gap-4 font-sans text-sm leading-normal text-foreground [color-scheme:dark] max-sm:[&_input]:text-base",
        "right-[max(24px,env(safe-area-inset-right))] bottom-[calc(max(32px,env(safe-area-inset-bottom))+var(--chat-viewport-bottom,0px))]",
        open
          ? "max-sm:inset-x-0 max-sm:top-[var(--chat-viewport-top,0px)] max-sm:bottom-auto max-sm:h-[var(--chat-viewport-height,100dvh)]"
          : "max-sm:right-0 max-sm:bottom-[calc(30dvh+var(--chat-viewport-bottom,0px))]",
      )}
      style={
        {
          "--chat-viewport-height": `${viewport.height}px`,
          "--chat-viewport-top": `${viewport.top}px`,
          "--chat-viewport-bottom": `${viewport.bottom}px`,
        } as CSSProperties
      }
    >
      <Card
        ref={setPanel}
        id={panelId}
        role="dialog"
        aria-modal={open && mobile}
        aria-labelledby={titleId}
        tabIndex={-1}
        hidden={!open}
        data-keyboard={keyboard || undefined}
        className={cn(
          "sm-chat-panel pointer-events-auto relative gap-0 overflow-hidden rounded-2xl py-0 shadow-[0_20px_70px_rgb(0_0_0/65%)] outline-none",
          "bg-background bg-[radial-gradient(700px_380px_at_20%_-10%,color-mix(in_oklch,var(--gold)_10%,transparent),transparent_60%)]",
          expanded
            ? "h-[min(900px,calc(var(--chat-viewport-height,100dvh)-136px))] w-[min(720px,calc(100vw-48px))]"
            : "h-[min(640px,calc(var(--chat-viewport-height,100dvh)-136px))] w-[380px]",
          "max-sm:size-full max-sm:rounded-none max-sm:border-0",
        )}
      >
        <ChatWindowContext.Provider value={panel}>
          <header
            className={cn(
              "flex min-h-12 shrink-0 items-center border-b border-border bg-card/80 backdrop-blur-sm max-sm:pt-[env(safe-area-inset-top)]",
              onBack ? "gap-1 pr-3 pl-1" : "gap-2 px-3",
            )}
          >
            {onBack && (
              <Button
                variant="ghost"
                size="icon"
                aria-label="Back to conversations"
                title="Back to chats"
                className={HEADER_BUTTON_CLASS}
                onClick={onBack}
              >
                <ChevronLeft className="size-5" />
              </Button>
            )}
            {onTitleClick ? (
              <Button
                variant="ghost"
                aria-label="Conversation members"
                aria-expanded={rosterOpen}
                className={cn(
                  HEADER_BUTTON_CLASS,
                  "h-auto min-w-0 flex-1 shrink justify-start gap-2.5 px-0 pr-2 font-normal",
                )}
                onClick={onTitleClick}
              >
                {heading}
              </Button>
            ) : (
              heading
            )}
            {headerActions}
            <Button
              variant="ghost"
              size="icon"
              title="Close chat"
              aria-label="Collapse chat"
              className={cn(
                HEADER_BUTTON_CLASS,
                "rounded-full text-muted-foreground",
              )}
              onClick={close}
            >
              <X />
            </Button>
          </header>
          {children}
        </ChatWindowContext.Provider>
      </Card>
      <div
        className={cn("relative flex items-center", open && "max-sm:hidden")}
      >
        {showHandlerTip && !open && (
          <div
            role="status"
            className="pointer-events-auto absolute bottom-full right-0 mb-4 w-64 rounded-xl border border-gold/50 bg-card p-4 text-sm text-foreground shadow-xl"
          >
            <button
              aria-label="Dismiss chat tip"
              className="float-right ml-2"
              onClick={() => setShowHandlerTip(false)}
            >
              <X size={16} />
            </button>
            Your handler is a tap away. Open this chat whenever you want to
            talk.
            <span
              aria-hidden="true"
              className="block text-right text-xl text-gold"
            >
              ↘
            </span>
          </div>
        )}
        <Button
          ref={launcher}
          variant="ghost"
          size="icon"
          aria-controls={panelId}
          aria-expanded={open}
          aria-label={`${open ? "Collapse" : "Open"} messages${unread ? `, ${unread} unread message${unread === 1 ? "" : "s"}` : ""}`}
          className={cn(
            "pointer-events-auto relative size-14 shrink-0 cursor-pointer rounded-full border border-gold/50 bg-card p-0 text-gold shadow-[0_8px_30px_rgb(0_0_0/55%)] hover:border-gold hover:bg-card/90 hover:text-gold motion-reduce:transition-none dark:hover:bg-card/90",
            "max-sm:h-12 max-sm:w-11 max-sm:rounded-l-full max-sm:rounded-r-none max-sm:border-r-0 max-sm:pl-1 max-sm:shadow-[0_6px_20px_rgb(0_0_0/45%)]",
          )}
          onClick={() => {
            setShowHandlerTip(false);
            if (open) close();
            else {
              previousFocus.current =
                document.activeElement as HTMLElement | null;
              setOpen(true);
            }
          }}
        >
          {open ? (
            <X className="size-6" />
          ) : ai ? (
            <Avatar name={title} ai size="md" />
          ) : (
            <MessageCircle className="size-6 max-sm:size-5" />
          )}
          {unread > 0 && !open && (
            <Badge
              variant={muted ? "secondary" : "default"}
              className="absolute -top-1 -right-1 h-5 min-w-5 px-1.5 font-semibold max-sm:right-auto max-sm:-left-1"
              aria-label={muted ? "Notifications muted" : undefined}
            >
              {unread > 99 ? "99+" : unread}
            </Badge>
          )}
        </Button>
      </div>
    </div>
  );
}
